import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { submitLead } from "@/lib/api";
import {
  BookOpen, ArrowRight, CheckCircle2, FileText, Download
} from "lucide-react";
import SectionLabel from "@/components/layout/SectionLabel";

const HIGHLIGHTS = [
  "How heat pump hot water actually works",
  "270L vs 320L sizing guide for Kiwi homes",
  "Replacing gas, electric or LPG hot water — what to expect",
  "What photos installers need to give you an accurate quote",
  "How to read the COP number on heat pump specs",
  "Real NZ running-cost estimates with worked examples",
];

export default function Guide() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.consent) {
      return toast.error("Name, email and consent are required.");
    }
    setSubmitting(true);
    try {
      await submitLead({
        type: "guide_download",
        name: form.name, email: form.email, phone: form.phone,
        consent: form.consent,
        notes: "Free NZ Home Hot Water Upgrade Guide request",
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch { toast.error("Submission failed. Please retry."); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="bg-white">
      <section className="border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <SectionLabel>Free guide</SectionLabel>
            <h1 className="font-heading mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0B132B]">
              The NZ Home Hot Water Upgrade Guide
            </h1>
            <p className="mt-5 text-lg text-[#475569] max-w-xl leading-relaxed">
              A plain-English PDF explaining heat pump hot water for Kiwi homeowners — what to ask, how to size it, and how to avoid common installation pitfalls.
            </p>
            <ul className="mt-8 space-y-3 max-w-lg">
              {HIGHLIGHTS.map((h, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-[#10B981] mt-0.5 shrink-0" />
                  <span className="text-[#0B132B]">{h}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5">
            {!done ? (
              <Card data-testid="guide-form" className="rounded-3xl p-8 border-[#E2E8F0] shadow-xl shadow-emerald-500/5">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#10B981]/10 text-[#10B981] mb-4">
                  <BookOpen className="h-6 w-6" />
                </div>
                <h2 className="font-heading text-2xl font-bold text-[#0B132B]">Get the free PDF</h2>
                <p className="text-sm text-[#475569] mt-1">We'll email it to you straight away.</p>
                <form onSubmit={submit} className="mt-6 space-y-4">
                  <div><Label className="text-xs">Name *</Label><Input data-testid="guide-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
                  <div><Label className="text-xs">Email *</Label><Input data-testid="guide-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
                  <div><Label className="text-xs">Phone (optional)</Label><Input data-testid="guide-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                  <div className="flex items-start gap-3 p-4 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0]">
                    <Checkbox data-testid="guide-consent" id="gd-c" checked={form.consent} onCheckedChange={(v) => setForm({ ...form, consent: !!v })} />
                    <Label htmlFor="gd-c" className="text-sm cursor-pointer leading-relaxed">
                      I agree for AeroMax Energy to email me the guide and follow up.
                    </Label>
                  </div>
                  <Button
                    type="submit"
                    data-testid="guide-submit"
                    disabled={submitting}
                    className="w-full rounded-full bg-[#10B981] hover:bg-[#059669] text-white py-6 font-semibold"
                  >
                    {submitting ? "Sending..." : "Send Me the Free Guide"} <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                  <p className="text-[10px] text-[#475569] text-center">
                    No spam. Unsubscribe any time.
                  </p>
                </form>
              </Card>
            ) : (
              <Card data-testid="guide-success" className="rounded-3xl p-10 border-[#E2E8F0] text-center bg-white">
                <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-[#10B981]/10 text-[#10B981] mb-5">
                  <Download className="h-8 w-8" />
                </div>
                <h2 className="font-heading text-3xl font-bold text-[#0B132B]">You're in!</h2>
                <p className="mt-3 text-[#475569]">
                  We'll email <span className="font-semibold text-[#0B132B]">{form.email}</span> with your free guide shortly. While you wait — see what you could save.
                </p>
                <div className="mt-6 flex flex-wrap gap-3 justify-center">
                  <Link to="/savings-calculator" className="inline-flex items-center gap-2 rounded-full bg-[#0B132B] hover:bg-[#050914] text-white px-6 py-3.5 font-semibold">
                    Run the Savings Calculator <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link to="/quiz" className="inline-flex items-center gap-2 rounded-full border-2 border-[#E2E8F0] text-[#0B132B] hover:border-[#0B132B] px-6 py-3.5 font-semibold transition">
                    Take the Quick Quiz
                  </Link>
                </div>
              </Card>
            )}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 text-center">
          <FileText className="h-7 w-7 text-[#10B981] mx-auto" />
          <p className="mt-3 text-sm text-[#475569]">
            The guide is provided for general information only. It is not financial or installation advice. Final sizing, pricing and installation details depend on a site assessment.
          </p>
        </div>
      </section>
    </div>
  );
}
